import {
  getCurrencies,
  updateCurrency,
  createCurrency,
  deleteCurrency,
  Currency,
} from 'common/src/api/currencies';
import { useEffect, useState } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Card,
  CardBody,
  Skeleton,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react';
import { ConfirmModal, PageHeader, PageLayout } from '../../../components/ui';

const SuperadminCurrencies = () => {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(
    null
  );
  const [newCurrency, setNewCurrency] = useState<{
    name: string;
    value: string;
    long_name: string;
  }>({ name: '', value: '', long_name: '' });
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const {
    isOpen: isNewOpen,
    onOpen: onNewOpen,
    onClose: onNewClose,
  } = useDisclosure();

  const fetchCurrencies = async () => {
    setLoading(true);
    try {
      const fetched = await getCurrencies();
      setCurrencies(fetched);
    } catch (error) {
      console.error('Error fetching currencies:', error);
      toast.error('Error al cargar monedas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrencies();
  }, []);

  const handleEdit = (currency: Currency) => {
    setSelectedCurrency(currency);
    onEditOpen();
  };

  const handleDelete = (currency: Currency) => {
    setSelectedCurrency(currency);
    onDeleteOpen();
  };

  const handleEditSubmit = async () => {
    if (!selectedCurrency) return;
    try {
      await updateCurrency(selectedCurrency.id, selectedCurrency);
      toast.success('Moneda actualizada correctamente');
      onEditClose();
      fetchCurrencies();
    } catch (error) {
      console.error('Error updating currency:', error);
      toast.error('Error al actualizar moneda');
    }
  };

  const handleNewSubmit = async () => {
    try {
      if (!newCurrency.name) {
        toast.error('El código de la moneda es requerido');
        return;
      } else if (!newCurrency.value) {
        toast.error('El valor de la moneda es requerido');
        return;
      } else if (!newCurrency.long_name) {
        toast.error('El nombre largo de la moneda es requerido');
        return;
      }
      await createCurrency(newCurrency);
      toast.success('Moneda creada correctamente');
      onNewClose();
      setNewCurrency({ name: '', value: '', long_name: '' });
      fetchCurrencies();
    } catch (error) {
      console.error('Error creating currency:', error);
      toast.error('Error al crear moneda');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedCurrency) return;
    try {
      await deleteCurrency(selectedCurrency.id);
      toast.success('Moneda eliminada correctamente');
      onDeleteClose();
      fetchCurrencies();
    } catch (error) {
      console.error('Error deleting currency:', error);
      toast.error('Error al eliminar moneda');
    }
  };

  const columns = [
    { name: 'Nombre (Código)', uid: 'name' },
    { name: 'Valor', uid: 'value' },
    { name: 'Nombre Largo', uid: 'long_name' },
    { name: 'Acciones', uid: 'actions' },
  ];

  const renderCell = (currency: Currency, columnKey: React.Key) => {
    switch (columnKey) {
      case 'actions':
        return (
          <div className='flex items-center gap-2'>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              onClick={() => handleEdit(currency)}
            >
              <Icon icon='tabler:edit' className='text-default-400' />
            </Button>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              color='danger'
              onClick={() => handleDelete(currency)}
            >
              <Icon icon='tabler:trash' className='text-danger-400' />
            </Button>
          </div>
        );
      default:
        return currency[columnKey as keyof Currency];
    }
  };

  return (
    <PageLayout>
      <PageHeader
        title='Monedas'
        actions={
          <Button color='primary' onClick={onNewOpen}>
            Añadir Moneda
          </Button>
        }
      />

      {loading ? (
        <div className=''>
          <div className='flex justify-between items-center '>
            <div className='space-y-2'>
              <Skeleton className='w-1/3 rounded-lg' />
            </div>
          </div>

          <Card className='w-full'>
            <CardBody>
              <div className='space-y-4'>
                <div className='grid grid-cols-4 gap-4'>
                  {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} className='h-8 w-full rounded-lg' />
                  ))}
                </div>

                {[...Array(5)].map((_, rowIndex) => (
                  <div key={rowIndex} className='grid grid-cols-4 gap-4'>
                    <Skeleton className='h-12 w-full rounded-lg' />
                    <Skeleton className='h-12 w-full rounded-lg' />
                    <Skeleton className='h-12 w-full rounded-lg' />
                    <div className='flex gap-2'>
                      <Skeleton className='h-8 w-8 rounded-lg' />
                      <Skeleton className='h-8 w-8 rounded-lg' />
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        </div>
      ) : (
        <Table removeWrapper aria-label='Currencies table'>
          <TableHeader columns={columns}>
            {(column) => (
              <TableColumn key={column.uid}>{column.name}</TableColumn>
            )}
          </TableHeader>
          <TableBody items={currencies}>
            {(item) => (
              <TableRow key={item.id}>
                {(columnKey) => (
                  <TableCell>{renderCell(item, columnKey)}</TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Modal isOpen={isEditOpen} onClose={onEditClose}>
        <ModalContent>
          <ModalHeader>Editar Moneda</ModalHeader>
          <ModalBody>
            <Input
              label='Código'
              value={selectedCurrency?.name || ''}
              onChange={(e) =>
                setSelectedCurrency({
                  ...selectedCurrency!,
                  name: e.target.value.toUpperCase(),
                })
              }
            />
            <Input
              label='Valor'
              value={selectedCurrency?.value || ''}
              onChange={(e) =>
                setSelectedCurrency({
                  ...selectedCurrency!,
                  value: e.target.value,
                })
              }
            />
            <Input
              label='Nombre Largo'
              value={selectedCurrency?.long_name || ''}
              onChange={(e) =>
                setSelectedCurrency({
                  ...selectedCurrency!,
                  long_name: e.target.value,
                })
              }
            />
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onClick={onEditClose}>
              Cancel
            </Button>
            <Button
              color='primary'
              onClick={handleEditSubmit}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleDeleteConfirm}
        title='Confirmar Eliminación'
        confirmLabel='Eliminar'
        cancelLabel='Cancel'
        isDestructive
      >
        ¿Estás seguro de que quieres eliminar {selectedCurrency?.name} ({selectedCurrency?.long_name})?
      </ConfirmModal>

      <Modal isOpen={isNewOpen} onClose={onNewClose}>
        <ModalContent>
          <ModalHeader>Nueva Moneda</ModalHeader>
          <ModalBody>
            <Input
              label='Código'
              value={newCurrency.name}
              onChange={(e) =>
                setNewCurrency({ ...newCurrency, name: e.target.value.toUpperCase() })
              }
              description='Código de la moneda (ej: USD, EUR)'
            />
            <Input
              label='Valor'
              value={newCurrency.value}
              onChange={(e) =>
                setNewCurrency({ ...newCurrency, value: e.target.value })
              }
              description='Símbolo o valor (ej: US$, €)'
            />
            <Input
              label='Nombre Largo'
              value={newCurrency.long_name}
              onChange={(e) =>
                setNewCurrency({ ...newCurrency, long_name: e.target.value })
              }
              description='Nombre completo (ej: Dólar Estadounidense)'
            />
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onClick={onNewClose}>
              Cancel
            </Button>
            <Button
              color='primary'
              onClick={handleNewSubmit}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageLayout>
  );
};

export default SuperadminCurrencies;
